/**
 * Task commerciale (tabella sales_activities) creato da un suggerimento AI
 * della chat, collegato al Lead/Cliente della conversazione. Non serve un
 * progetto: il progetto resta un'opzione per chi lavora a commessa.
 *
 * Convenzione colonne: come la scheda Lead (lead_id) e il pannello chat
 * (client_id); le letture usano `.or(lead_id.eq.X,client_id.eq.X)`, quindi
 * valorizzarle entrambe fa comparire il Task commerciale ovunque.
 */
/**
 * Da dove nasce il Task commerciale: un suggerimento AI della chat WhatsApp
 * (con id in whatsapp_agent_requests) oppure una risposta dell'assistente in una
 * conversazione Instagram/Messenger (senza id: non c'e' un suggerimento da chiudere).
 */
export interface SalesTaskSource {
  id?: string;
  title: string;
  description: string;
  original_text?: string | null;
  sender_name?: string | null;
}

export interface SalesTaskInput {
  title: string;
  description: string;
  dueDate: Date;
  clientId: string;
  userId: string;
  organizationId: string;
  assignedTo?: string | null;
}

export interface SalesTaskInsert {
  title: string;
  description: string;
  type: 'task';
  status: 'pending';
  due_date: string;
  lead_id: string;
  client_id: string;
  user_id: string;
  assigned_to: string;
  organization_id: string;
}

/** Scadenza proposta: il giorno lavorativo successivo alle 9:00 (ora locale). */
export function defaultDueDate(now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0, 0, 0);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2); // sabato -> lunedi'
  if (day === 0) d.setDate(d.getDate() + 1); // domenica -> lunedi'
  return d;
}

/** Descrizione di partenza: richiesta + messaggio originale del cliente. */
export function suggestionDescription(req: Pick<SalesTaskSource, 'description' | 'original_text' | 'sender_name'>): string {
  const parts: string[] = [];
  if (req.description?.trim()) parts.push(req.description.trim());
  if (req.original_text?.trim()) {
    parts.push(`Messaggio del cliente${req.sender_name ? ` (${req.sender_name})` : ''}: ${req.original_text.trim()}`);
  }
  return parts.join('\n\n');
}

export function buildSalesTaskInsert(input: SalesTaskInput): SalesTaskInsert {
  const title = input.title.trim();
  if (!title) throw new Error('Il titolo del Task commerciale è obbligatorio');
  if (!input.clientId) throw new Error('Collega prima la chat a un Lead o Cliente');
  return {
    title: title.slice(0, 500),
    description: input.description.trim(),
    type: 'task',
    status: 'pending',
    due_date: input.dueDate.toISOString(),
    lead_id: input.clientId,
    client_id: input.clientId,
    user_id: input.userId,
    assigned_to: input.assignedTo || input.userId,
    organization_id: input.organizationId,
  };
}
